
import {jsPDF} from "jspdf";
import {renderProfileChartCanvas, CHART_WIDTH, CHART_HEIGHT} from "./ProfileChartRenderer.js";
import {buildCrossSectionReportFilename} from "./reportFilename.js";
import {formatNumber, formatGeneratedAt} from "./reportFormat.js";
import {drawReportLogo} from "./reportLogo.js";

import {t} from "../i18n/index.js";
// Builds and downloads the Tværprofiler PDF report entirely client-side via
// jsPDF, drawing every element (header, metadata, overview image, chart
// grid, page numbering) directly - REPLACES the previous window.print()-
// based export entirely (see the "Næste iteration" task's own sections 1-3
// and 13-16). Two hard problems with window.print() made this switch
// necessary, both confirmed by directly re-reading the previous
// implementation rather than guessing:
//
// 1. Browser-native print header/footer (page title/URL/date/page number)
//    is a user-facing print-dialog CHECKBOX in every major browser - there
//    is no CSS/JS mechanism anywhere to suppress it from the page's own
//    side. That made it structurally impossible to satisfy the requirement
//    that the PDF never reveal the local dev URL/environment.
// 2. The report modal's own `box` element carried an inline
//    `max-height:90vh; overflow-y:auto` that was never neutralised for
//    print (only its child was) - Chrome's print engine does not
//    paginate PAST a scroll/clip ancestor, it just prints whatever fit in
//    that first "viewport". That was the direct, confirmed root cause of
//    the "13 profiler" reported but only P01-P09 actually printed.
//
// Building the PDF directly sidesteps both at once: there is no browser
// print surface at all (doc.save() is a plain client-side Blob download,
// not a print dialog), and pagination is explicit code below that measures
// remaining page space before placing each chart row - it can never
// silently drop content regardless of profile count (task section 16:
// tested against 3/9/13/25/50).
const PAGE_WIDTH = 210, PAGE_HEIGHT = 297; // A4 portrait, mm
const MARGIN = 14;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const CONTENT_BOTTOM = PAGE_HEIGHT - MARGIN;
const FOOTER_BASELINE_Y = PAGE_HEIGHT - 8;
const FOOTER_RULE_Y = PAGE_HEIGHT - 12;

const CHART_COLUMNS = 3;
const CHART_GUTTER = 4;
const CHART_ROW_GAP = 4;
const CHART_CARD_WIDTH = (CONTENT_WIDTH - CHART_GUTTER * (CHART_COLUMNS - 1)) / CHART_COLUMNS;
// GEOMETRIC 1:1 CORRECTNESS (task section 12): the card's height is derived
// from CHART_CARD_WIDTH via the chart canvas's OWN fixed pixel aspect ratio
// (CHART_WIDTH/CHART_HEIGHT), never chosen independently - this is exactly
// what keeps ProfileChartRenderer.js's pixel-space 1:1 claim physically true
// on the actual printed page (a uniform scale factor applied to both axes),
// instead of only being true in numeric axis labels.
const CHART_CARD_HEIGHT = CHART_CARD_WIDTH / (CHART_WIDTH / CHART_HEIGHT);
const CHART_ROW_HEIGHT = CHART_CARD_HEIGHT + CHART_ROW_GAP;

function metaLine(doc, x, y, label, value){
	doc.setFont(undefined, "normal");
	doc.setFontSize(7.5);
	doc.setTextColor(120, 120, 120);
	doc.text(label.toUpperCase(), x, y);
	doc.setFont(undefined, "bold");
	doc.setFontSize(10);
	doc.setTextColor(30, 30, 30);
	doc.text(value, x, y + 4.3);
}

// Header + metadata + overview image (page 1 only, task section 4: this is
// the ONLY header the report has, no browser-added chrome duplicating any
// of it) - returns the cursorY to continue drawing from.
function drawFrontPageHeader(doc, {stations, overviewCanvas, meta}){
	let y = MARGIN;

	doc.setFont(undefined, "bold");
	doc.setFontSize(20);
	doc.setTextColor(15, 15, 15);
	doc.text(t("Tværprofilrapport"), MARGIN, y + 7);

	doc.setFont(undefined, "normal");
	doc.setFontSize(11);
	doc.setTextColor(70, 70, 70);
	doc.text(meta.featureName, MARGIN, y + 13);

	// IT_thirty four-logoet i stedet for tekstmærket "IT34 · SmartSurvey"
	const logoX = drawReportLogo(doc, PAGE_WIDTH, MARGIN, y);
	doc.setFont(undefined, "normal");
	doc.setFontSize(8.5);
	doc.setTextColor(130, 130, 130);
	doc.text(t("Punktsky-baseret tværprofilanalyse"), logoX - 4, y + 9, {align: "right"});

	y += 17;
	doc.setDrawColor(47, 143, 214);
	doc.setLineWidth(0.8);
	doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
	y += 7;

	// task section 3: the generation timestamp appears here and ONLY here -
	// nowhere else in the document (no browser print date/time exists any
	// more, since there is no browser print surface at all).
	const items = [
		[t("Genereret"), formatGeneratedAt()],
		meta.dataSource ? [t("Datakilde"), meta.dataSource] : null,
		meta.coordinateSystem ? [t("Koordinatsystem"), meta.coordinateSystem] : null,
		[t("Profilinterval"), `${formatNumber(meta.interval, 2)} m`],
		[t("Profillængde"), `${formatNumber(meta.profileLength, 2)} m`],
		[t("Profilbånd"), `${formatNumber(meta.band, 2)} m`],
		[t("Antal profiler"), String(stations.length)],
	].filter(Boolean);

	const metaCols = 4;
	const metaColWidth = CONTENT_WIDTH / metaCols;
	items.forEach(([label, value], i) => {
		const col = i % metaCols, row = Math.floor(i / metaCols);
		metaLine(doc, MARGIN + col * metaColWidth, y + row * 11, label, value);
	});
	const metaRows = Math.ceil(items.length / metaCols);
	y += metaRows * 11 + 2;

	// task section 11: the "positiv retning" explanation now appears ONCE,
	// here, instead of being repeated on every single chart caption.
	doc.setFont(undefined, "italic");
	doc.setFontSize(8.5);
	doc.setTextColor(110, 110, 110);
	doc.text(t("Positiv retning er mod venstre set i forløbsretningen."), MARGIN, y);
	y += 5;

	doc.setDrawColor(220, 220, 220);
	doc.setLineWidth(0.3);
	doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
	y += 5;

	const overviewHeight = CONTENT_WIDTH * (overviewCanvas.height / overviewCanvas.width);
	doc.setDrawColor(200, 200, 200);
	doc.setLineWidth(0.2);
	doc.addImage(overviewCanvas, "PNG", MARGIN, y, CONTENT_WIDTH, overviewHeight, undefined, "FAST");
	doc.rect(MARGIN, y, CONTENT_WIDTH, overviewHeight);
	y += overviewHeight + 7;

	return y;
}

// Minimal continuation header (task section 14: later pages need not repeat
// the overview/full metadata - priority goes to chart-plot space) for page
// 2, 3, ... - just enough to identify the report without repeating anything
// already shown on page 1.
function drawContinuationHeader(doc, meta){
	let y = MARGIN;
	doc.setFont(undefined, "bold");
	doc.setFontSize(11);
	doc.setTextColor(15, 15, 15);
	doc.text(t("Tværprofilrapport — {0} (fortsat)", meta.featureName), MARGIN, y + 4);
	y += 7;
	doc.setDrawColor(47, 143, 214);
	doc.setLineWidth(0.5);
	doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
	y += 6;
	return y;
}

function drawFooters(doc){
	const pageCount = doc.internal.getNumberOfPages();
	for(let p = 1; p <= pageCount; p++){
		doc.setPage(p);
		doc.setDrawColor(225, 225, 225);
		doc.setLineWidth(0.2);
		doc.line(MARGIN, FOOTER_RULE_Y, PAGE_WIDTH - MARGIN, FOOTER_RULE_Y);

		doc.setFont(undefined, "normal");
		doc.setFontSize(8);
		doc.setTextColor(140, 140, 140);
		doc.text("IT34 · SmartSurvey", MARGIN, FOOTER_BASELINE_Y);
		doc.text(t("Side {0} af {1}", p, pageCount), PAGE_WIDTH - MARGIN, FOOTER_BASELINE_Y, {align: "right"});
	}
}

// {stations: [{name, profilePoints}], overviewCanvas, meta: {featureName,
// interval, profileLength, band, dataSource, coordinateSystem}}
export function buildCrossSectionPdf({stations, overviewCanvas, meta}){
	const doc = new jsPDF({unit: "mm", format: "a4", orientation: "portrait"});

	let cursorY = drawFrontPageHeader(doc, {stations, overviewCanvas, meta});

	// Explicit, per-row space check (task sections 13/14/16) - NEVER
	// hardcoded around an expected profile count. Every row is placed only
	// after confirming it fits; if not, a fresh page (with its own slim
	// continuation header) is started first - so a chart can never be cut
	// across a page break, and no profile is ever dropped regardless of how
	// many there are.
	let i = 0;
	while(i < stations.length){
		if(cursorY + CHART_ROW_HEIGHT > CONTENT_BOTTOM){
			doc.addPage();
			cursorY = drawContinuationHeader(doc, meta);
		}

		for(let col = 0; col < CHART_COLUMNS && i < stations.length; col++, i++){
			const station = stations[i];
			const {canvas} = renderProfileChartCanvas(station, meta.profileLength);
			const x = MARGIN + col * (CHART_CARD_WIDTH + CHART_GUTTER);
			doc.addImage(canvas, "PNG", x, cursorY, CHART_CARD_WIDTH, CHART_CARD_HEIGHT, undefined, "FAST");
			doc.setDrawColor(210, 210, 210);
			doc.setLineWidth(0.2);
			doc.rect(x, cursorY, CHART_CARD_WIDTH, CHART_CARD_HEIGHT);
		}

		cursorY += CHART_ROW_HEIGHT;
	}

	drawFooters(doc);

	const filename = buildCrossSectionReportFilename(meta.dataSource);
	doc.save(filename);
	return filename;
}
